import { useEffect, useCallback, ChangeEvent, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
	errorColor,
	secondaryColor,
	warningColor,
} from "../../../../core/theme";
import { useAppDispatch } from "../../../../common/hooks/default";
import {
	getTimeseriesByTender,
	triggerTimeserieCleaning,
	createTimeserie,
	createPricingsByTs,
	generateTsWithActiveRespyModel,
} from "../../tenderForm.thunk";
import {
	Box,
	CircularProgress,
	Radio,
	Tooltip,
	Button,
	SxProps,
	Theme,
	IconButton,
	Divider,
	Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import {
	selectTender,
	selectTsBySite,
	selectTsBySiteLoader,
	selectTsTabError,
} from "../../tenderForm.selector";
import { If } from "../../../../common/components/If";
import { NO_CLEANED_STATUS, TS_SOURCE } from "../../tenderForm.constant";
import {
	TenderGraphStatus,
	Timeserie,
	TimeseriesList,
	TsSource,
	getTimeserieStatusColor,
} from "../../tenderForm.module";
import { PrimaryButton } from "../../../../common/components/CustomButton";
import { useTenderActions } from "../../../tender_page/hooks/useTenderActions";
import ReplayIcon from "@mui/icons-material/Replay";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { TimeserieModal } from "./TimeserieModal";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { enqueueSnackbar } from "notistack";
import { useFormik } from "formik";
import { Site } from "../../../sites/sites.module";
import { RootState } from "../../../../core/rootReducers";
import { mergeStyles } from "../../../../common/style";
import { TenderGraphAction } from "../TenderGraphAction";
import { AddTimeseriesButton } from "./AddTimeseriesButton";
import { UploadFileModal } from "../UploadFileModal";
import { linkFileToTender } from "../../../../common/files/files.thunk";
import {
	UploadedFile,
	UploadingFile,
	isUploadedFile,
} from "../../../../common/files/files.module";
import Checkbox from "@mui/material/Checkbox";
import WarningCleaningModal from "../WarningCleaningModal";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { PortfolioTimeserieConfigTable } from "./PortfolioTimeserieConfigTable";
import { ExtractionConfidenceBadge } from "../../../../common/components/ExtractionConfidenceBadge";
import * as _ from "lodash";
import { selectFeatureFlags } from "../../../parameters/ParametersPage.selector";

export interface TimeseriesTableProps {
	siteIds?: number[];
	tenderId?: number;
}

export interface TSInUse {
	asset_id?: string;
	site_id?: string;
	ts_id?: number;
	clean?: boolean;
	tourbillon_tag?: string;
}

export interface TSToClean {
	timeserieId: number;
	siteId: number;
	isNew: boolean;
}

const style: {
	[key: string]: SxProps<Theme> | { [key: string]: SxProps<Theme> };
} = {
	buttonContainer: {
		marginBottom: "16px",
		marginRight: "16px",
	},
	errorContainer: {
		marginBottom: "16px",
		marginRight: "16px",
		color: "red",
	},
	tableContainer: {
		backgroundColor: "transparent",
	},
	headerCellContainer: {
		backgroundColor: secondaryColor,
		color: "#000",
		fontWeight: 700,
		fontSize: 12,
		paddingLeft: 0,
		"& th": {
			fontWeight: 700,
			padding: "12px 16px",
		},
	},
	table: {
		tableLayout: "fixed",
		minWidth: 650,
	},
	siteNameCol: {
		width: "25%",
	},
	tsNameCol: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	copyIcon: {
		cursor: "pointer",
	},
	siteHeader: {
		justifyContent: "center",
		alignItems: "center",
		display: "flex",
		flex: 1,
		width: "100%",
		gap: "12px",
	},
	siteTable: {
		borderTop: "1px solid #000",
	},
	cell: {
		border: "0",
	},
	line: { border: "1px solid rgba(0, 0, 0, .2)" },
	noline: { border: "0px" },
	dividerWrapper: { marginTop: "32px" },
};

export const TimeseriesTable = (props: TimeseriesTableProps) => {
	const { siteIds } = props;
	const tender = useSelector(selectTender);

	const actions = tender?.actions_blotter?.filter((element: any) =>
		["TRIGGER_RENEWEX", "TRIGGER_TS_IMPORTER"].includes(element.action)
	);
	const [currentSiteToAddTs, setCurrentSiteToAddTs] = useState<number | null>(
		null
	);
	const [currentSiteToUploadFile, setCurrentSiteToUploadFile] = useState<
		number | null
	>(null);

	const [warningsModal, setWarningsModal] = useState<
		{ check: boolean; message: string }[]
	>([]);

	const dispatch = useAppDispatch();
	const [tsInUse, setTsInUse] = useState<{ [key: string]: TSInUse }>({});
	const [portfolioTsInUses, setPortfolioTsInUses] = useState<
		Record<string, Record<string, Timeserie>>
	>({});

	const [tsToClean, setTsToClean] = useState<{
		[key: string]: TSToClean;
	}>({});

	const tsBySite = useSelector(selectTsBySite);
	const error = useSelector((state: RootState) =>
		selectTsTabError(state, "createPricingByTs")
	);
	const loading = useSelector(selectTsBySiteLoader);

	useEffect(() => {
		if (Object.keys(tsInUse).length === 0) {
			let formInitValues: any = {};

			tsBySite.sites.forEach((ts: TimeseriesList) => {
				if (ts.site_info?.asset_id) {
					let info = ts.ts?.filter(
						(element) =>
							element.tourbillon_tag === ts.site_info?.ts_in_use
					);
					formInitValues[ts.site_info?.asset_id] = {
						asset_id: ts.site_info.asset_id,
						site_id: ts.site_info.id,
						tourbillon_tag: info.length
							? info[0].tourbillon_tag
							: "Cluster",
						clean: info.length
							? info[0]?.status == "RAW"
								? false
								: true
							: false,
						ts_id: info.length ? info[0].id : undefined,
					};
				}
			});
			setTsInUse(formInitValues);
			setPortfolioTsInUses(tsBySite.portfolios);
		}
	}, [tsBySite]);

	const formikTSInfo: any = useFormik({
		initialValues: {},
		enableReinitialize: true,
		onSubmit: () => {
			let tsList = Object.values(tsInUse);
			dispatch(
				createPricingsByTs(
					{
						tender_id: tender?.id,
						ts_list: tsList,
						portfolios: portfolioTsInUses,
					},
					tender?.id
				)
			);
		},
	});

	const formikTSClean: any = useFormik({
		initialValues: {},
		enableReinitialize: true,
		onSubmit: () => {
			dispatch(
				triggerTimeserieCleaning(
					Object.values(tsToClean),
					tender?.id || 0
				)
			);
			setTsToClean({});
		},
	});

	const handleInUseChange = (
		event: ChangeEvent<HTMLInputElement>,
		site: Site,
		ts: any
	) => {
		setTsInUse((prev: any) => ({
			...prev,
			[event.target.name]: {
				asset_id: site.asset_id,
				site_id: site.id,
				tourbillon_tag: event.target.value,
				clean: ts.status === "RAW" ? false : true,
				ts_id: ts.id,
			},
		}));
	};

	const handleCleanChange = (
		event: ChangeEvent<HTMLInputElement>,
		site: Site,
		ts: any
	) => {
		setTsToClean((prev: any) => {
			if (event.target.name in prev) {
				delete prev[event.target.name];
				return { ...prev };
			}
			return {
				...prev,
				[event.target.name]: {
					timeserieId: ts.id,
					siteId: site.id,
					isNew: ts.can_cleaning_be_retried ? false : true,
				},
			};
		});
	};

	useEffect(() => {
		if (tender && tsBySite.sites.length === 0) {
			dispatch(getTimeseriesByTender({ tenderId: tender.id }));
		}
	}, []);

	const triggerCleaning = useCallback(
		(timeserieId: number, siteId: number, isNew: boolean) => {
			dispatch(
				triggerTimeserieCleaning(
					[
						{
							timeserieId,
							siteId,
							isNew,
						},
					],
					tender?.id || 0
				)
			);
		},
		[dispatch, siteIds]
	);

	const create = useCallback(
		(siteId: number, timeserieTag: string) => {
			dispatch(createTimeserie(siteId, timeserieTag, tender?.id || 0));
		},
		[dispatch, siteIds]
	);

	const linkUploadedFile = useCallback(
		(file: UploadingFile | UploadedFile) => {
			if (isUploadedFile(file) && tender?.id) {
				dispatch(linkFileToTender(file.id, tender?.id));
			}
		},
		[dispatch, tender]
	);

	const { tenderAction, tenderActionModal } = useTenderActions(tender);

	const features = useSelector(selectFeatureFlags);
	const clusterSupportedInCountry = (countryIsoCode: string) =>
		features[`${countryIsoCode}_cluster_support`.toLowerCase()] ?? true;

	const onGenerateNewTsWithRespyModel = useCallback(
		(site_id: number) => {
			if (tender) {
				dispatch(generateTsWithActiveRespyModel(tender.id, site_id));
			}
		},
		[tender]
	);

	return (
		<Box>
			<Box
				display="flex"
				justifyContent="flex-end"
				sx={style.buttonContainer}
			>
				<form onSubmit={formikTSClean.handleSubmit}>
					<PrimaryButton
						type="submit"
						color="secondary"
						sx={{
							marginRight: "16px",
						}}
					>
						{"Clean"}
					</PrimaryButton>
				</form>
				{tender &&
					TenderGraphAction(
						tender,
						Object.values(tsInUse),
						TenderGraphStatus[tender.graph_status]
					)}

				<form onSubmit={formikTSInfo.handleSubmit}>
					<PrimaryButton
						type="submit"
						color="secondary"
						sx={{
							marginRight: "16px",
						}}
					>
						{"Validate"}
					</PrimaryButton>
				</form>

				<PrimaryButton
					type="button"
					color="secondary"
					sx={{
						marginRight: "16px",
						width: 60,
					}}
					onClick={() => {
						if (siteIds && siteIds.length > 0) {
							dispatch(
								getTimeseriesByTender({
									tenderId: tender?.id || 0,
								})
							);
						}
					}}
				>
					<ReplayIcon />
				</PrimaryButton>
				{actions?.map((action: any, key: number) => (
					<PrimaryButton
						key={action + key}
						text={action.display_name}
						type="button"
						color="secondary"
						sx={{
							marginRight: "16px",
							width: 180,
						}}
						onClick={() => {
							tenderAction(action);
						}}
					></PrimaryButton>
				))}
			</Box>
			<If condition={!!error}>
				<Box
					display="flex"
					justifyContent="flex-center"
					sx={style.errorContainer}
				>
					{error}
				</Box>
			</If>

			<If condition={loading}>
				<Box display={"flex"} justifyContent={"center"}>
					<CircularProgress data-testid="loader" />
				</Box>
			</If>

			<If condition={!loading}>
				<TableContainer component={Paper} sx={style.tableContainer}>
					<Table sx={style.table} aria-label="simple table">
						<TableHead>
							<TableRow
								sx={mergeStyles(
									style.headerCellContainer,
									style.line
								)}
							>
								<TableCell
									sx={style.siteNameCol}
									component="th"
									scope="col"
								>
									Site
								</TableCell>
								<TableCell
									component="th"
									scope="col"
									sx={style.tsNameCol}
								>
									Ts Name
								</TableCell>
								<TableCell component="th" scope="col">
									Tourbillon tag
								</TableCell>
								<TableCell component="th" scope="col">
									Source
								</TableCell>
								<TableCell component="th" scope="col">
									Confidence
								</TableCell>
								<TableCell component="th" scope="col">
									Status
								</TableCell>
								<TableCell component="th" scope="col">
									In use
								</TableCell>
								<TableCell component="th" scope="col">
									Clean
								</TableCell>
								<TableCell component="th" scope="col">
									Source&nbsp;File
								</TableCell>
								<TableCell component="th" scope="col">
									Row&nbsp;Offset
								</TableCell>
								<TableCell component="th" scope="col">
									Tab
								</TableCell>
								<TableCell component="th" scope="col">
									Production&nbsp;value
								</TableCell>
								<TableCell component="th" scope="col">
									Actions
								</TableCell>
							</TableRow>
						</TableHead>

						{tsBySite.sites.map((row: TimeseriesList) => (
							<TableBody
								sx={style.siteTable}
								key={row.site_info.name}
							>
								<If condition={row.ts.length > 0}>
									{row.ts?.map(
										(ts: Timeserie, index: number) => (
											<TableRow
												sx={
													ts.name === "Cluster"
														? style.line
														: style.noline
												}
												key={ts.tourbillon_tag}
											>
												<If condition={index === 0}>
													<TableCell
														sx={style.cell}
														component="th"
														rowSpan={row.ts.length}
														scope="rowgroup"
													>
														<Box
															sx={
																style.siteHeader
															}
														>
															{row.site_info.name}
															<AddTimeseriesButton
																hasActiveRespyModel={
																	row.has_active_respy_model
																}
																onGenerateNewTsWithRespyModel={() =>
																	onGenerateNewTsWithRespyModel(
																		row
																			.site_info
																			.id
																	)
																}
																onAddManually={() =>
																	setCurrentSiteToAddTs(
																		row
																			.site_info
																			.id
																	)
																}
																onUploadFile={() => {
																	setCurrentSiteToUploadFile(
																		row
																			.site_info
																			.id
																	);
																}}
															/>
														</Box>
													</TableCell>
												</If>
												<TableCell
													component="td"
													scope="row"
													sx={mergeStyles(
														style.tsNameCol,
														style.cell
													)}
												>
													<Tooltip title={ts.name}>
														<span>{ts.name}</span>
													</Tooltip>
												</TableCell>
												<TableCell
													component="td"
													scope="row"
													sx={style.cell}
												>
													<Box sx={style.tsNameCol}>
														<ContentCopyIcon
															sx={style.copyIcon}
															onClick={() => {
																navigator.clipboard.writeText(
																	ts.tourbillon_tag ||
																		""
																);
																enqueueSnackbar(
																	"copied",
																	{
																		variant:
																			"success",
																		autoHideDuration: 1000,
																	}
																);
															}}
														/>
														<Tooltip
															title={
																ts.tourbillon_tag
															}
														>
															<span>
																{
																	ts.tourbillon_tag
																}
															</span>
														</Tooltip>
													</Box>
												</TableCell>
												<TableCell
													sx={style.cell}
													component="td"
												>
													{
														TS_SOURCE[
															TsSource[ts.source]
														]
													}
												</TableCell>
												<TableCell
													sx={style.cell}
													component="td"
												>
													<ExtractionConfidenceBadge
														value={
															ts.extraction_confidence
														}
														description={
															ts.extraction_confidence_description
														}
													/>
												</TableCell>
												<TableCell
													sx={{
														...style.cell,
														color: getTimeserieStatusColor(
															ts
														),
													}}
													component="td"
												>
													<If
														condition={
															ts.timeserie_error
														}
													>
														<Tooltip
															title={
																ts.timeserie_error
															}
														>
															<Box
																sx={{
																	color: errorColor,
																}}
															>
																{ts.status}
																<HelpOutlineIcon
																	sx={{
																		fontSize:
																			"16px",
																	}}
																/>
															</Box>
														</Tooltip>
													</If>
													<If
														condition={
															!ts.timeserie_error
														}
													>
														{ts.status}
														{ts.has_warnings && (
															<IconButton
																sx={{
																	color: warningColor,
																}}
															>
																<ReportProblemOutlinedIcon
																	onClick={() =>
																		setWarningsModal(
																			ts.warnings
																				? ts.warnings
																				: []
																		)
																	}
																/>
															</IconButton>
														)}
													</If>
												</TableCell>

												<TableCell
													sx={style.cell}
													component="td"
												>
													<Radio
														checked={
															tsInUse[
																row.site_info
																	.asset_id ||
																	""
															]
																?.tourbillon_tag ===
															ts.tourbillon_tag
														}
														onChange={(
															event: ChangeEvent<HTMLInputElement>
														) =>
															handleInUseChange(
																event,
																row.site_info,
																ts
															)
														}
														value={
															ts.tourbillon_tag
														}
														name={
															row.site_info
																.asset_id
														}
														inputProps={{
															"aria-label":
																ts.tourbillon_tag,
														}}
													/>
												</TableCell>
												<TableCell
													sx={style.cell}
													component="td"
												>
													<Checkbox
														disabled={
															NO_CLEANED_STATUS.includes(
																ts.status
															) ||
															!clusterSupportedInCountry(
																row.site_info
																	.country_iso2_code
															)
														}
														checked={
															!!tsToClean[
																(ts.name ||
																	"") +
																	row
																		.site_info
																		.id
															]
														}
														name={
															(ts.name || "") +
															row.site_info.id
														}
														onChange={(
															event: ChangeEvent<HTMLInputElement>
														) =>
															handleCleanChange(
																event,
																row.site_info,
																ts
															)
														}
													/>
												</TableCell>

												<TableCell
													sx={style.cell}
													component="td"
												></TableCell>
												<TableCell
													sx={style.cell}
													component="td"
												></TableCell>
												<TableCell
													sx={style.cell}
													component="td"
												></TableCell>
												<TableCell
													sx={style.cell}
													component="td"
												></TableCell>
												<TableCell
													sx={style.cell}
													component="td"
												>
													<If
														condition={
															ts.can_be_cleaned &&
															clusterSupportedInCountry(
																row.site_info
																	.country_iso2_code
															)
														}
													>
														<Button
															variant="text"
															onClick={() => {
																triggerCleaning(
																	ts.id,
																	row
																		.site_info
																		.id,
																	true
																);
															}}
														>
															Clean
														</Button>
													</If>
													<If
														condition={
															ts.can_cleaning_be_retried
														}
													>
														<Button
															variant="text"
															color="warning"
															onClick={() => {
																triggerCleaning(
																	ts.id,
																	row
																		.site_info
																		.id,
																	false
																);
															}}
														>
															Retry
														</Button>
													</If>
												</TableCell>
											</TableRow>
										)
									)}
								</If>
								<If condition={row.ts.length === 0}>
									<TableRow key={row.site_info.name}>
										<TableCell
											sx={style.cell}
											component="th"
											rowSpan={row.ts.length}
											scope="rowgroup"
										>
											<Box sx={style.siteHeader}>
												{row.site_info.name}
												<AddIcon
													sx={style.addIcon}
													onClick={() =>
														setCurrentSiteToAddTs(
															row.site_info.id
														)
													}
												/>
											</Box>
										</TableCell>
										<TableCell
											component="td"
											scope="row"
											sx={mergeStyles(
												style.tsNameCol,
												style.cell
											)}
										></TableCell>
										<TableCell
											sx={style.cell}
											component="td"
										></TableCell>
										<TableCell
											sx={style.cell}
											component="td"
										></TableCell>
										<TableCell
											sx={style.cell}
											component="td"
										></TableCell>
										<TableCell
											sx={style.cell}
											component="td"
										></TableCell>
										<TableCell
											sx={style.cell}
											component="td"
										></TableCell>
										<TableCell
											sx={style.cell}
											component="td"
										></TableCell>
										<TableCell
											sx={style.cell}
											component="td"
										></TableCell>
									</TableRow>
								</If>
							</TableBody>
						))}
					</Table>
				</TableContainer>
				<Box sx={style.dividerWrapper}>
					<Divider textAlign="left">
						<Typography variant="h3">
							<b>Portfolios</b>
						</Typography>
					</Divider>
				</Box>
				{tender?.portfolios?.map((portfolio) => (
					<PortfolioTimeserieConfigTable
						key={portfolio.id}
						portfolio={portfolio}
						timeseries={_.sortBy(
							tsBySite.sites.filter((ts) =>
								portfolio.sites
									.map((site) => site.asset_id)
									.includes(ts.site_info.asset_id)
							),
							"site_info.asset_id"
						)}
						portfolioTsInUse={portfolioTsInUses}
						setPortfolioTsInUse={setPortfolioTsInUses}
						sitesTsInUses={tsInUse}
					/>
				))}
			</If>
			<TimeserieModal
				onClose={() => setCurrentSiteToAddTs(null)}
				isOpen={!!currentSiteToAddTs}
				onSubmit={(tag: string) => {
					if (currentSiteToAddTs) {
						setCurrentSiteToAddTs(null);
						create(currentSiteToAddTs, tag);
					}
				}}
			/>
			<UploadFileModal
				onClose={() => setCurrentSiteToUploadFile(null)}
				isOpen={!!currentSiteToUploadFile}
				onSubmit={linkUploadedFile}
				associatedSiteId={currentSiteToUploadFile}
			/>
			<WarningCleaningModal
				onClose={() => setWarningsModal([])}
				isOpen={!!warningsModal.length}
				warnings={warningsModal}
			/>
			{tenderActionModal}
		</Box>
	);
};
